/*
 *   This content is licensed according to the W3C Software License at
 *   https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 *
 *   File:   radio.js
 *
 *   Desc:   Radio group widget that implements ARIA Authoring Practices
 */

'use strict'

export class RovingTabindex {
  groupNode: Element
  radioButtons: SVGElement[]
  firstRadioButton: SVGElement | null
  lastRadioButton: SVGElement | null
  mode: 'aria-radio' | 'focus-only'

  constructor(groupNode: Element, options?: { mode?: 'aria-radio' | 'focus-only' }) {
    this.groupNode = groupNode

    this.radioButtons = []

    this.firstRadioButton = null
    this.lastRadioButton = null
    this.mode = options?.mode ?? 'aria-radio'

    const rbs = this.groupNode.querySelectorAll<SVGElement>(
      '[role=radio]:not([aria-disabled=true])',
    )

    for (let i = 0; i < rbs.length; i++) {
      const rb = rbs[i]

      rb.tabIndex = -1
      if (this.mode === 'aria-radio') {
        rb.setAttribute('aria-checked', 'false')
      }

      rb.addEventListener('keydown', this.handleKeydown.bind(this))
      rb.addEventListener('click', this.handleClick.bind(this))
      rb.addEventListener('focus', this.handleFocus.bind(this))
      rb.addEventListener('blur', this.handleBlur.bind(this))

      this.radioButtons.push(rb)

      if (!this.firstRadioButton) {
        this.firstRadioButton = rb
      }
      this.lastRadioButton = rb
    }
    if (this.firstRadioButton) {
      this.firstRadioButton.tabIndex = 0
    }
  }

  setChecked(currentItem: SVGElement) {
    for (let i = 0; i < this.radioButtons.length; i++) {
      const rb = this.radioButtons[i]
      rb.setAttribute('aria-checked', 'false')
      rb.tabIndex = -1
    }
    currentItem.setAttribute('aria-checked', 'true')
    currentItem.tabIndex = 0
    currentItem.focus()
  }

  setFocused(currentItem: SVGElement) {
    for (let i = 0; i < this.radioButtons.length; i++) {
      const rb = this.radioButtons[i]
      rb.tabIndex = -1
    }
    currentItem.tabIndex = 0
    currentItem.focus()
  }

  setCheckedToPreviousItem(currentItem: SVGElement) {
    let index: number

    if (currentItem === this.firstRadioButton) {
      if (this.lastRadioButton) {
        this.setChecked(this.lastRadioButton)
      }
    } else {
      index = this.radioButtons.indexOf(currentItem)
      this.setChecked(this.radioButtons[index - 1])
    }
  }

  setCheckedToNextItem(currentItem: SVGElement) {
    let index: number

    if (currentItem === this.lastRadioButton) {
      if (this.firstRadioButton) {
        this.setChecked(this.firstRadioButton)
      }
    } else {
      index = this.radioButtons.indexOf(currentItem)
      this.setChecked(this.radioButtons[index + 1])
    }
  }

  setFocusedToPreviousItem(currentItem: SVGElement) {
    let index: number

    if (currentItem === this.firstRadioButton) {
      if (this.lastRadioButton) {
        this.setFocused(this.lastRadioButton)
      }
    } else {
      index = this.radioButtons.indexOf(currentItem)
      this.setFocused(this.radioButtons[index - 1])
    }
  }

  setFocusedToNextItem(currentItem: SVGElement) {
    let index: number

    if (currentItem === this.lastRadioButton) {
      if (this.firstRadioButton) {
        this.setFocused(this.firstRadioButton)
      }
    } else {
      index = this.radioButtons.indexOf(currentItem)
      this.setFocused(this.radioButtons[index + 1])
    }
  }

  /* EVENT HANDLERS */

  handleKeydown(event: KeyboardEvent) {
    const tgt = event.currentTarget as SVGElement
    let flag = false

    switch (event.key) {
      case ' ':
        if (this.mode === 'aria-radio') {
          this.setChecked(tgt)
          flag = true
        }
        break

      case 'Up':
      case 'ArrowUp':
      case 'Left':
      case 'ArrowLeft':
        if (this.mode === 'aria-radio') {
          this.setCheckedToPreviousItem(tgt)
        } else {
          this.setFocusedToPreviousItem(tgt)
        }
        flag = true
        break

      case 'Down':
      case 'ArrowDown':
      case 'Right':
      case 'ArrowRight':
        if (this.mode === 'aria-radio') {
          this.setCheckedToNextItem(tgt)
        } else {
          this.setFocusedToNextItem(tgt)
        }
        flag = true
        break

      default:
        break
    }

    if (flag) {
      event.stopPropagation()
      event.preventDefault()
    }
  }

  handleClick(event: MouseEvent) {
    const tgt = event.currentTarget as SVGElement
    if (this.mode === 'aria-radio') {
      this.setChecked(tgt)
    } else {
      this.setFocused(tgt)
    }
  }

  handleFocus(event: FocusEvent) {
    const tgt = event.currentTarget as SVGElement
    tgt.classList.add('focus')
  }

  handleBlur(event: FocusEvent) {
    const tgt = event.currentTarget as SVGElement
    tgt.classList.remove('focus')
  }
}
